<template>
    <div
        class="flex gap-1 md:gap-2 justify-center items-center"
        @paste="handleContainerPaste"
    >
        <div v-for="(digit, index) in digits" :key="index">
            <label :for="`otp-digit-${index}`" class="sr-only"
                >Digit {{ index + 1 }} out of {{ digits.length }} for One Time
                Password
            </label>
            <input
                :key="index"
                :ref="(el) => setInputRef(el, index)"
                v-model="digits[index]"
                type="text"
                inputmode="numeric"
                autocomplete="one-time-code"
                :maxlength="1"
                :id="`otp-digit-${index}`"
                class="flex-shrink-0 flex-grow-0 w-10 h-10 md:w-16 md:h-16 min-w-10 max-w-10 min-h-10 max-h-10 md:min-w-16 md:max-w-16 md:min-h-16 md:max-h-16 text-center text-base sm:text-lg md:text-2xl font-semibold border-2 rounded-md transition-all"
                :class="[
                    hasError
                        ? '!border-red-600 focus:border-red-500 focus:ring-red-200'
                        : 'border-gray-500',
                ]"
                @input="handleInput(index, $event)"
                @keydown="handleKeydown(index, $event)"
                @paste="handlePaste(index, $event)"
                @focus="handleFocus(index)"
            />
        </div>
    </div>
    <div
        v-if="hasError && errorMessage"
        class="mt-2 text-sm text-red-600 text-center"
    >
        {{ errorMessage }}
    </div>
</template>

<script lang="ts" setup>
import { ref, computed, nextTick, watch } from "vue";

interface Props {
    length?: number;
    modelValue?: string;
    hasError?: boolean;
    errorMessage?: string;
    disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    length: 6,
    modelValue: "",
    hasError: false,
    errorMessage: "",
    disabled: false,
});

const emit = defineEmits<{
    "update:modelValue": [value: string];
    complete: [value: string];
}>();

// Create reactive array for each digit
const digits = ref<string[]>(Array(props.length).fill(""));
const inputElements: { [key: number]: HTMLInputElement } = {};

// Set input references
const setInputRef = (el: any, index: number) => {
    if (el) {
        inputElements[index] = el;
    }
};

// Computed value that combines all digits
const combinedValue = computed(() => digits.value.join(""));

// Watch for external modelValue changes
watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue !== combinedValue.value) {
            const chars = newValue.split("").slice(0, props.length);
            digits.value = Array(props.length)
                .fill("")
                .map((_, i) => chars[i] || "");
        }
    },
    { immediate: true }
);

// Watch for digit changes and emit updates
watch(combinedValue, (newValue) => {
    emit("update:modelValue", newValue);

    // Emit complete event when all digits are filled
    if (newValue.length === props.length) {
        emit("complete", newValue);
    }
});

const handleInput = (index: number, event: Event) => {
    if (props.disabled) return;

    const target = event.target as HTMLInputElement;
    const value = target.value;

    // Only allow numeric characters
    const numericValue = value.replace(/[^0-9]/g, "");

    if (numericValue !== value) {
        target.value = numericValue;
    }

    digits.value[index] = numericValue;

    // Auto-focus next input if current one is filled
    if (numericValue && index < props.length - 1) {
        nextTick(() => {
            inputElements[index + 1]?.focus();
        });
    }
};

const handleKeydown = (index: number, event: KeyboardEvent) => {
    if (props.disabled) return;

    // Handle Ctrl+V / Cmd+V paste
    if ((event.ctrlKey || event.metaKey) && event.key === "v") {
        // Check if clipboard API is available
        if (navigator.clipboard && navigator.clipboard.readText) {
            // Only prevent default if we can handle it with Clipboard API
            event.preventDefault();

            // Use the modern Clipboard API
            navigator.clipboard
                .readText()
                .then((text) => {
                    const numericData = text.replace(/[^0-9]/g, "");

                    if (numericData) {
                        // Clear all inputs first
                        digits.value = Array(props.length).fill("");

                        // Fill inputs with pasted data
                        for (
                            let i = 0;
                            i < props.length && i < numericData.length;
                            i++
                        ) {
                            digits.value[i] = numericData[i];
                        }

                        // Focus the next empty input or the last input
                        const nextEmptyIndex = digits.value.findIndex(
                            (digit) => !digit
                        );
                        const focusIndex =
                            nextEmptyIndex !== -1
                                ? nextEmptyIndex
                                : props.length - 1;

                        nextTick(() => {
                            inputElements[focusIndex]?.focus();
                        });
                    }
                })
                .catch((err) => {
                    // Clipboard read failed
                });
        } else {
            // Don't prevent default - let the browser handle it and trigger paste events
        }

        return;
    }

    // Handle backspace
    if (event.key === "Backspace") {
        if (!digits.value[index] && index > 0) {
            // If current input is empty, focus previous input and clear it
            nextTick(() => {
                inputElements[index - 1]?.focus();
                digits.value[index - 1] = "";
            });
        } else {
            // Clear current input
            digits.value[index] = "";
        }
    }

    // Handle left arrow key
    if (event.key === "ArrowLeft" && index > 0) {
        inputElements[index - 1]?.focus();
    }

    // Handle right arrow key
    if (event.key === "ArrowRight" && index < props.length - 1) {
        inputElements[index + 1]?.focus();
    }

    // Allow Enter to submit the form natively
    if (event.key === "Enter") {
        // Find the closest parent form and submit it
        const input = event.target as HTMLInputElement;
        const form = input.closest('form');
        if (form && typeof form.requestSubmit === 'function') {
            form.requestSubmit();
        } else if (form) {
            form.submit();
        }
        return;
    }

    // Prevent non-numeric input (except for special keys)
    if (
        !/[0-9]/.test(event.key) &&
        !["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Enter"].includes(event.key)
    ) {
        event.preventDefault();
    }
};

const handlePaste = (index: number, event: ClipboardEvent) => {
    if (props.disabled) return;

    event.preventDefault();
    const pasteData = event.clipboardData?.getData("text") || "";
    const numericData = pasteData.replace(/[^0-9]/g, "");


    if (numericData) {
        // Clear all inputs first
        digits.value = Array(props.length).fill("");

        // Fill inputs with pasted data starting from the current index
        for (let i = 0; i < props.length && i < numericData.length; i++) {
            digits.value[i] = numericData[i];
        }

        // Focus the next empty input or the last input
        const nextEmptyIndex = digits.value.findIndex((digit) => !digit);
        const focusIndex =
            nextEmptyIndex !== -1 ? nextEmptyIndex : props.length - 1;

        nextTick(() => {
            inputElements[focusIndex]?.focus();
        });
    }
};

const handleContainerPaste = (event: ClipboardEvent) => {
    if (props.disabled) return;

    event.preventDefault();
    event.stopPropagation();

    const pasteData = event.clipboardData?.getData("text") || "";
    const numericData = pasteData.replace(/[^0-9]/g, "");

    if (numericData) {
        // Clear all inputs first
        digits.value = Array(props.length).fill("");

        // Fill inputs with pasted data
        for (let i = 0; i < props.length && i < numericData.length; i++) {
            digits.value[i] = numericData[i];
        }

        // Focus the next empty input or the last input
        const nextEmptyIndex = digits.value.findIndex((digit) => !digit);
        const focusIndex =
            nextEmptyIndex !== -1 ? nextEmptyIndex : props.length - 1;

        nextTick(() => {
            inputElements[focusIndex]?.focus();
        });
    }
};

const handleFocus = (index: number) => {
    // Select all text when input is focused
    nextTick(() => {
        inputElements[index]?.select();
    });
};

// Public methods
const focus = () => {
    const firstEmptyIndex = digits.value.findIndex((digit) => !digit);
    const focusIndex = firstEmptyIndex !== -1 ? firstEmptyIndex : 0;
    inputElements[focusIndex]?.focus();
};

const clear = () => {
    digits.value = Array(props.length).fill("");
    inputElements[0]?.focus();
};

// Expose methods to parent
defineExpose({
    focus,
    clear,
});
</script>
